<template>
  <div class="disposal-statistics-container">
    <!-- 顶部选项卡区域 -->
    <div class="header-section">
      <el-tabs v-model="activeMainTab" class="main-tabs">
        <el-tab-pane label="处置项目报表统计" name="table">
          <div class="tab-content"></div>
        </el-tab-pane>
        <el-tab-pane label="处置项目图表分析" name="chart">
          <div class="tab-content"></div>
        </el-tab-pane>
      </el-tabs>
    </div>

    <!-- 筛选区域 -->
    <el-card class="filter-section">
      <div class="filter-container">
        <div class="filter-left">
          <div class="filter-item">
            <span class="filter-label">处置项目名称：</span>
            <el-input
              v-model="filters.disposalName"
              placeholder="请输入处置项目名称"
              size="mini"
              clearable
              style="width: 160px;"
            />
          </div>
          
          <div class="filter-item">
            <span class="filter-label">所属科室：</span>
            <el-select
              v-model="filters.department"
              multiple
              collapse-tags
              placeholder="请选择科室"
              size="mini"
              style="width: 200px;"
            >
              <el-option
                v-for="item in deptOptions"
                :key="item.id"
                :label="item.deptName"
                :value="item.id"
              />
            </el-select>
          </div>
          
          <!-- 时间范围选择器 -->
          <div class="filter-item">
            <span class="filter-label">时间范围：</span>
            <el-date-picker
              v-model="filters.dateRange"
              type="datetimerange"
              range-separator="至"
              start-placeholder="开始日期"
              end-placeholder="结束日期"
              size="mini"
              style="width: 360px;"
              value-format="yyyy-MM-dd HH:mm:ss"
              format="yyyy-MM-dd HH:mm:ss"
              :default-time="['00:00:00', '23:59:59']"
              @change="handleDateChange"
            />
          </div>
        </div>
        
        <div class="filter-right">
          <el-button type="primary" size="mini" @click="handleSearch" icon="el-icon-Ssearch">查询</el-button>
          <el-button type="warning" size="mini" @click="handleReset" icon="el-icon-refresh">重置</el-button>
          <el-button type="success" size="mini" @click="handleExport" icon="el-icon-download" :disabled="true">导出</el-button>
        </div>
      </div>
    </el-card>

    <!-- 主内容区域 -->
    <div class="main-content-section">
      <!-- 表格区域 -->
      <div v-if="activeMainTab === 'table'">
        <!-- 加载状态 -->
        <div v-if="loading" class="loading-container">
          <el-skeleton :rows="5" animated />
        </div>
        
        <!-- 空状态 -->
        <div v-else-if="tableData.length === 0" class="empty-container">
          <el-empty description="暂无数据" />
        </div>
        
        <!-- 表格内容 -->
        <el-card v-else class="table-section">
          <div class="table-container">
            <el-table
              :data="currentTableData"
              border
              stripe
              size="mini"
              :height="tableMaxHeight"
              :header-cell-style="{ background: '#f5f7fa', color: '#606266', 'text-align': 'center' }"
              :cell-style="{ 'text-align': 'center' }"
            >
              <el-table-column
                prop="costname"
                label="处置项目名称"
                align="center"
                min-width="150"
              >
                <template slot="header" slot-scope="scope">
                  <el-tooltip 
                    class="item" 
                    effect="dark" 
                    content="处置项目名称：直接展示服务名称（如 '针灸''推拿''雾化治疗'），无需编码冗余字段" 
                    placement="top"
                  >
                    <span>处置项目名称</span>
                  </el-tooltip>
                </template>
              </el-table-column>
              
              <el-table-column
                prop="deptName"
                label="所属科室"
                align="center"
                min-width="120"
              >
                <template slot="header" slot-scope="scope">
                  <el-tooltip 
                    class="item" 
                    effect="dark" 
                    content="所属科室：对应归属科室（如 '中医科''内科'），便于科室维度汇总" 
                    placement="top"
                  >
                    <span>所属科室</span>
                  </el-tooltip>
                </template>
              </el-table-column>
              
              <el-table-column
                prop="standardPrice"
                label="收费标准（元）"
                align="center"
                min-width="120"
              >
                <template slot="header" slot-scope="scope">
                  <el-tooltip 
                    class="item" 
                    effect="dark" 
                    content="收费标准（元）：项目对外统一单价，直观呈现定价" 
                    placement="top"
                  >
                    <span>收费标准（元）</span>
                  </el-tooltip>
                </template>
                <template slot-scope="scope">
                  {{ formatCurrency(scope.row.standardPrice) }}
                </template>
              </el-table-column>
              
              <el-table-column
                prop="totalTimes"
                label="服务总次数"
                align="center"
                min-width="120"
              >
                <template slot="header" slot-scope="scope">
                  <el-tooltip 
                    class="item" 
                    effect="dark" 
                    content="服务总次数：统计周期内完成的总服务次数（1 人多次累计），体现运营规模" 
                    placement="top"
                  >
                    <span>服务总次数</span>
                  </el-tooltip>
                </template>
              </el-table-column>
              
              <el-table-column
                prop="totalNum"
                label="接诊人次"
                align="center"
                min-width="120"
              >
                <template slot="header" slot-scope="scope">
                  <el-tooltip 
                    class="item" 
                    effect="dark" 
                    content="接诊人次：接受该项目服务的患者总数（排除退单），反映项目热度" 
                    placement="top"
                  >
                    <span>接诊人次</span>
                  </el-tooltip>
                </template>
              </el-table-column>
              
              <el-table-column
                prop="totalAmt"
                label="总营业收入（元）"
                align="center"
                min-width="140"
              >
                <template slot="header" slot-scope="scope">
                  <el-tooltip 
                    class="item" 
                    effect="dark" 
                    content="总营业收入（元）：服务总次数 × 收费标准，直接体现创收能力" 
                    placement="top"
                  >
                    <span>总营业收入（元）</span>
                  </el-tooltip>
                </template>
                <template slot-scope="scope">
                  {{ formatCurrency(scope.row.totalAmt) }}
                </template>
              </el-table-column>
              
              <el-table-column
                prop="costAmt"
                label="单位成本（元）"
                align="center"
                min-width="120"
              >
                <template slot="header" slot-scope="scope">
                  <el-tooltip 
                    class="item" 
                    effect="dark" 
                    content="单位成本（元）：单次服务的直接成本（耗材、设备分摊等），简化成本核算" 
                    placement="top"
                  >
                    <span>单位成本（元）</span>
                  </el-tooltip>
                </template>
                <template slot-scope="scope">
                  {{ formatCurrency(scope.row.costAmt) }}
                </template>
              </el-table-column>
              
              <el-table-column
                prop="totalCostAmt"
                label="总成本（元）"
                align="center"
                min-width="120"
              >
                <template slot="header" slot-scope="scope">
                  <el-tooltip 
                    class="item" 
                    effect="dark" 
                    content="总成本（元）：服务总次数 × 单位成本，无需分摊间接成本，降低核算复杂度" 
                    placement="top"
                  >
                    <span>总成本（元）</span>
                  </el-tooltip>
                </template>
                <template slot-scope="scope">
                  {{ formatCurrency(scope.row.totalCostAmt) }}
                </template>
              </el-table-column>
              
              <el-table-column
                prop="grossProfit"
                label="项目毛利（元）"
                align="center"
                min-width="120"
              >
                <template slot="header" slot-scope="scope">
                  <el-tooltip 
                    class="item" 
                    effect="dark" 
                    content="项目毛利（元）：总营业收入 - 总成本，核心盈利数据" 
                    placement="top"
                  >
                    <span>项目毛利（元）</span>
                  </el-tooltip>
                </template>
                <template slot-scope="scope">
                  {{ formatCurrency(scope.row.grossProfit) }}
                </template>
              </el-table-column>
              
              <el-table-column
                prop="grossProfitRate"
                label="毛利率（%）"
                align="center"
                min-width="120"
              >
                <template slot="header" slot-scope="scope">
                  <el-tooltip 
                    class="item" 
                    effect="dark" 
                    content="毛利率（%）：（项目毛利 ÷ 总营业收入）×100%，保留 1 位小数，便于快速对比" 
                    placement="top"
                  >
                    <span>毛利率（%）</span>
                  </el-tooltip>
                </template>
                <template slot-scope="scope">
                  {{ scope.row.grossProfitRate }}%
                </template>
              </el-table-column>
              
              <el-table-column
                label="操作"
                align="center"
                fixed="right"
                width="80"
              >
                <template slot-scope="scope">
                  <el-button type="text" size="mini" @click="handleViewDetail(scope.row)" icon="el-icon-Nofullscreen">详情</el-button>
                </template>
              </el-table-column>
            </el-table>
            
            <!-- 分页 -->
            <div class="pagination-container left-pagination">
              <el-pagination
                @size-change="handleSizeChange"
                @current-change="handleCurrentChange"
                :current-page="pageNum"
                :page-sizes="[10, 20, 50, 100]"
                :page-size="pageSize"
                layout="total, sizes, prev, pager, next, jumper"
                :total="total"
                small
              />
            </div>
          </div>
        </el-card>
      </div>

      <!-- 图表区域 -->
      <div v-else-if="activeMainTab === 'chart'">
        <!-- 图表控制区域 -->
        <el-card class="chart-control-section">
          <div class="chart-controls">
            <el-select v-model="chartType" size="mini" style="width: 200px;" @change="updateMainChart">
              <el-option label="极坐标柱状图" value="polarBar"></el-option>
              <el-option label="折线图" value="line"></el-option>
              <el-option label="折线图（动态排序折线图）" value="lineSort"></el-option>
              <el-option label="饼图" value="pie"></el-option>
              <el-option label="柱状图" value="bar"></el-option>
              <el-option label="相关矩阵（热力图，矩阵坐标系）" value="heatmap"></el-option>
              <el-option label="桑基图（桑基图左对齐布局）" value="sankey"></el-option>
            </el-select>
            
            <el-select v-model="chartField" size="mini" style="width: 150px; margin-left: 10px;" @change="updateMainChart">
              <el-option label="总营业收入" value="totalAmt"></el-option>
              <el-option label="服务总次数" value="totalTimes"></el-option>
              <el-option label="接诊人次" value="totalNum"></el-option>
              <el-option label="毛利率" value="grossProfitRate"></el-option>
              <el-option label="项目毛利" value="grossProfit"></el-option>
            </el-select>
          </div>
        </el-card>
        
        <!-- 图表内容区域 -->
        <div class="chart-content-section">
          <!-- 加载状态 -->
          <div v-if="loading" class="chart-loading">
            <el-skeleton :rows="3" animated />
          </div>
          
          <!-- 空状态 -->
          <div v-else-if="tableData.length === 0" class="chart-empty">
            <el-empty description="暂无数据，请先筛选获取数据" />
          </div>
          
          <!-- 图表容器 -->
          <div v-else class="chart-container">
            <!-- 添加明确的宽度和高度设置 -->
            <div ref="chartContainer" style="width: 100%; height: 100%; min-height: 400px;"></div>
          </div>
        </div>
      </div>
    </div>

    <!-- 详情模态框 -->
    <el-dialog
      :title="`${detailData.costname} - 处置项目详细分析`"
      :visible.sync="detailDialogVisible"
      width="60%"
      top="8vh"
      custom-class="detail-dialog"
      :before-close="handleCloseDetail"
      :close-on-click-modal="false"
    >
      <div class="detail-container">
        <!-- 左侧区域：头像、科室名称、字段列表 -->
        <div class="detail-left">
          <!-- 科室头像和名称 -->
          <div class="dept-header">
            <div class="dept-avatar-section">
              <el-avatar
                :size="60"
                :src="detailData.deptImgUrl"
                v-if="detailData.deptImgUrl"
                class="dept-avatar"
              >
                {{ detailData.deptName ? detailData.deptName.charAt(0) : '' }}
              </el-avatar>
              <el-avatar
                :size="40"
                v-else
                class="dept-avatar"
              >
                {{ detailData.deptName ? detailData.deptName.charAt(0) : '' }}
              </el-avatar>
              <div class="dept-info">
                <div class="dept-status">
                  <el-tag type="success" size="small">{{ detailData.costname }}</el-tag>
                </div>
              </div>
            </div>
          </div>
          
          <!-- 分割线 -->
          <el-divider content-position="left">分析维度</el-divider>
          
          <!-- 字段列表 -->
          <div class="field-list">
            <el-menu
              default-active="1"
              class="field-menu"
              @select="handleFieldSelect"
              background-color="#f8f9fa"
              text-color="#606266"
              active-text-color="#409EFF"
            >
              <el-menu-item index="1">
                <i class="el-icon-user"></i>
                <span>接诊人次</span>
                <span class="field-count">({{ detailData.totalNum || 0 }})</span>
              </el-menu-item>
              <el-menu-item index="2">
                <i class="el-icon-document"></i>
                <span>服务总次数</span>
                <span class="field-count">({{ detailData.totalTimes || 0 }})</span>
              </el-menu-item>
              <el-menu-item index="3">
                <i class="el-icon-money"></i>
                <span>总营业收入</span>
                <span class="field-count">({{ formatCurrency(detailData.totalAmt) || 0 }})</span>
              </el-menu-item>
              <el-menu-item index="4">
                <i class="el-icon-xmjg"></i>
                <span>总成本</span>
                <span class="field-count">({{ formatCurrency(detailData.totalCostAmt) || 0 }})</span>
              </el-menu-item>
              <el-menu-item index="5">
                <i class="el-icon-data-line"></i>
                <span>项目毛利</span>
                <span class="field-count">({{ formatCurrency(detailData.grossProfit) || 0 }})</span>
              </el-menu-item>
              <el-menu-item index="6">
                <i class="el-icon-coin"></i>
                <span>毛利率</span>
                <span class="field-count">({{ detailData.grossProfitRate || 0 }}%)</span>
              </el-menu-item>
            </el-menu>
          </div>
        </div>
        
        <!-- 右侧区域：详情和图表 -->
        <div class="detail-right">
          <!-- 选项卡：详细统计、图表统计 -->
          <el-tabs v-model="activeTab" type="card" class="detail-tabs">
            <el-tab-pane label="详细统计" name="detail">
              <div class="detail-content">
                <!-- 详细指标 -->
                <div class="indicator-section">
                  <el-descriptions title="详细指标" :column="2" border>
                    <el-descriptions-item label="处置项目名称">{{ detailData.costname }}</el-descriptions-item>
                    <el-descriptions-item label="所属科室">{{ detailData.deptName }}</el-descriptions-item>
                    <el-descriptions-item label="收费标准（元）">{{ formatCurrency(detailData.standardPrice) }}</el-descriptions-item>
                    <el-descriptions-item label="服务总次数">{{ detailData.totalTimes }}</el-descriptions-item>
                    <el-descriptions-item label="接诊人次">{{ detailData.totalNum }}</el-descriptions-item>
                    <el-descriptions-item label="总营业收入（元）">{{ formatCurrency(detailData.totalAmt) }}</el-descriptions-item>
                    <el-descriptions-item label="单位成本（元）">{{ formatCurrency(detailData.costAmt) }}</el-descriptions-item>
                    <el-descriptions-item label="总成本（元）">{{ formatCurrency(detailData.totalCostAmt) }}</el-descriptions-item>
                    <el-descriptions-item label="项目毛利（元）">{{ formatCurrency(detailData.grossProfit) }}</el-descriptions-item>
                    <el-descriptions-item label="毛利率（%）">{{ detailData.grossProfitRate }}%</el-descriptions-item>
                  </el-descriptions>
                </div>
              </div>
            </el-tab-pane>
            
            <el-tab-pane label="图表统计" name="chart" :disabled="true">
              <div class="chart-content">
                <!-- 图表形状选择 -->
                <div class="chart-controls">
                  <el-select v-model="detailChartType" size="mini" style="width: 150px;" @change="updateDetailChart">
                    <el-option label="极坐标柱状图" value="polarBar"></el-option>
                    <el-option label="折线图" value="line"></el-option>
                    <el-option label="饼图" value="pie"></el-option>
                    <el-option label="柱状图" value="bar"></el-option>
                    <el-option label="散点图" value="scatter"></el-option>
                  </el-select>
                </div>
                
                <!-- 图表容器 -->
                <div class="detail-chart-container" ref="detailChartContainer"></div>
              </div>
            </el-tab-pane>
          </el-tabs>
        </div>
      </div>
      
      <!-- 底部按钮 -->
      <span slot="footer" class="dialog-footer">
        <el-button size="mini" @click="handleCloseDetail">关闭</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import { disposeStatis } from "@/api/statisticalReport/Report";
import { findAttribute } from "@/api/user.js"
import * as echarts from 'echarts'

export default {
  name: 'DisposalStatistics',
  data() {
    // 计算默认时间范围：结束时间为当天23:59:59，开始时间为一周前00:00:00
      const getCurrentTime = () => {
        const now = new Date();
        return {
          year: now.getFullYear(),
          month: now.getMonth() + 1,
          date: now.getDate(),
          hours: now.getHours(),
          minutes: now.getMinutes(),
          seconds: now.getSeconds()
        };
      };
      
      const current = getCurrentTime();
      
      // 结束时间：当天23:59:59
      const endTime = new Date(current.year, current.month - 1, current.date, 23, 59, 59);
      
      // 开始时间：一周前00:00:00
      const startTime = new Date(current.year, current.month - 1, current.date - 6, 0, 0, 0);
    return {
      // 主选项卡
      activeMainTab: 'table',
      
      // 筛选条件
          filters: {
            disposalName: '',
            department: [],
            dateRange: [
              this.formatDateTime(startTime, 'yyyy-MM-dd HH:mm:ss'),  // 一周前00:00:00
              this.formatDateTime(endTime, 'yyyy-MM-dd HH:mm:ss')     // 当天23:59:59
            ]
          },
      
      // 部门选项
      deptOptions: [],
      userInfo: {},
      
      // 表格数据
      tableData: [],
      loading: false,
      
      // 分页
      pageNum: 1,
      pageSize: 15,
      total: 0,
      
      // 表格最大高度
      tableMaxHeight: 'calc(100vh - 320px)',
      
      // 图表相关
      chartType: 'pie',
      chartField: 'totalAmt',
      chartInstance: null,
      
      // 详情模态框
      detailDialogVisible: false,
      detailData: {},
      activeTab: 'detail',
      detailChartType: 'polarBar',
      detailChartInstance: null,
      activeField: '1'
    }
  },
  computed: {
    currentTableData() {
      const { pageNum, pageSize } = this
      const start = (pageNum - 1) * pageSize
      const end = start + pageSize
      return this.tableData.slice(start, end)
    }
  },
  mounted() {
    this.updateTableHeight();
    this.loadUserInfo();
    this.fetchDeptOptions();
    window.addEventListener('resize', this.updateTableHeight)
    
    // 页面加载时获取数据
    this.fetchTableData();
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.updateTableHeight)
    if (this.chartInstance) {
      this.chartInstance.dispose()
    }
    if (this.detailChartInstance) {
      this.detailChartInstance.dispose()
    }
  },
  methods: {
    // 更新表格高度
    updateTableHeight() {
      const headerHeight = 60
      const filterHeight = 80
      const paginationHeight = 60
      const padding = 40
      this.tableMaxHeight = `calc(100vh - ${headerHeight + filterHeight + paginationHeight + padding}px)`
    },
    // 格式化日期时间
      formatDateTime(date, format = 'yyyy-MM-dd HH:mm:ss') {
        if (!date) return ''
        const d = new Date(date)
        const year = d.getFullYear()
        const month = (d.getMonth() + 1).toString().padStart(2, '0')
        const day = d.getDate().toString().padStart(2, '0')
        const hours = d.getHours().toString().padStart(2, '0')
        const minutes = d.getMinutes().toString().padStart(2, '0')
        const seconds = d.getSeconds().toString().padStart(2, '0')
        
        if (format === 'yyyy-MM-dd') {
          return `${year}-${month}-${day}`
        } else if (format === 'yyyy-MM-dd HH:mm:ss') {
          return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`
        }
        return `${year}-${month}-${day}`
      },
    // 格式化日期
    formatDate(date, format = 'yyyy-MM-dd') {
      if (!date) return ''
      const d = new Date(date)
      const year = d.getFullYear()
      const month = (d.getMonth() + 1).toString().padStart(2, '0')
      const day = d.getDate().toString().padStart(2, '0')
      
      if (format === 'yyyy-MM-dd') {
        return `${year}-${month}-${day}`
      }
      return `${year}-${month}-${day}`
    },
    
    // 格式化货币金额
    formatCurrency(value) {
      if (value === null || value === undefined || value === '') {
        return '￥0.00'
      }
      
      // 转换为数字
      const num = Number(value)
      if (isNaN(num)) {
        return '￥0.00'
      }
      
      // 格式化为保留2位小数
      const fixedValue = num.toFixed(2)
      
      // 使用银行用的千位分隔符
      const parts = fixedValue.split('.')
      parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',')
      
      return `￥${parts.join('.')}`
    },
    
    // 日期变化处理
    handleDateChange() {
      // 可以在这里添加日期变化后的逻辑
      console.log('日期变化:', this.filters.dateRange)
    },
    
    // 加载用户信息
    loadUserInfo() {
      try {
        const userInfoStr = localStorage.getItem('userInfo')
        if (userInfoStr) {
          this.userInfo = JSON.parse(userInfoStr)
        }
      } catch (error) {
        console.error('加载用户信息失败:', error)
      }
    },
    
    // 获取科室选项
    async fetchDeptOptions() {
      try {
        const clinicId = this.userInfo.clinicId
        if (clinicId) {
          const res = await findAttribute({
            clinicId
          })
          if (res && res.data) {
            this.deptOptions = res.data
            console.log('获取科室选项:', this.deptOptions)
          }
        } else {
          this.deptOptions = []
        }
      } catch (error) {
        console.error('获取科室选项失败:', error)
        this.deptOptions = []
      }
    },
    
    // 获取表格数据
    async fetchTableData() {
      this.loading = true
      try {
        // 准备请求参数
        const params = {
          pageNum: this.pageNum,
          pageSize: this.pageSize
        }
        
        // 添加处置项目名称筛选
        if (this.filters.disposalName) {
          params.costName = this.filters.disposalName
        }
        
        // 添加科室筛选 - 使用 deptId
        if (this.filters.department && this.filters.department.length > 0) {
          params.deptId = this.filters.department[0] // 取第一个科室
        }
        
        // 添加时间筛选
        if (this.filters.dateRange && this.filters.dateRange.length === 2) {
          params.startTime = this.filters.dateRange[0]
          params.endTime = this.filters.dateRange[1]
        }
        
        console.log('请求参数:', params)
        
        // 调用接口获取数据
        const res = await disposeStatis(params)
        
        if (res && res.data) {
          // 转换接口数据，计算毛利和毛利率
          this.tableData = this.transformApiData(res.data.content || [])
          this.total = res.data.totalSize || 0
          
          // 如果有图表实例，更新图表
          if (this.activeMainTab === 'chart' && this.chartInstance) {
            this.updateMainChart()
          }
          
          this.$message({
            message: '已成功获取' + this.total + '条处置项目报表数据。',
            type: 'success',
            duration: 1500
          })
        } else {
          this.tableData = []
          this.total = 0
          this.$message.warning('暂无数据')
        }
      } catch (error) {
        console.error('获取数据失败:', error)
        this.$message.error('数据加载失败')
        this.tableData = []
        this.total = 0
      } finally {
        this.loading = false
      }
    },
    
    // 转换接口数据
    transformApiData(apiData) {
      if (!apiData || !Array.isArray(apiData)) {
        return []
      }
      
      return apiData.map(item => {
        // 计算项目毛利
        const grossProfit = (item.totalAmt || 0) - (item.totalCostAmt || 0)
        
        // 计算毛利率
        let grossProfitRate = 0
        if (item.totalAmt && item.totalAmt > 0) {
          grossProfitRate = ((grossProfit / item.totalAmt) * 100).toFixed(1)
        }
        
        return {
          ...item,
          grossProfit: grossProfit,
          grossProfitRate: grossProfitRate
        }
      })
    },
    
    // 查询
    handleSearch() {
      console.log('查询条件:', this.filters)
      this.pageNum = 1
      this.fetchTableData()
    },
    
    // 重置
    handleReset() {
      // 重置筛选条件
      this.filters.disposalName = ''
      this.filters.department = []
      
      // 重置时间为默认一周
      const endTime = new Date();
      const startTime = new Date();
      startTime.setDate(endTime.getDate() - 7);
      
      this.filters.dateRange = [
        this.formatDate(startTime, 'yyyy-MM-dd'),
        this.formatDate(endTime, 'yyyy-MM-dd')
      ]
      
      this.pageNum = 1
      this.fetchTableData()
      this.$message.info('已重置筛选条件')
    },
    
    // 导出
    handleExport() {
      this.$message.success('导出功能开发中')
    },
    
    // 分页大小改变
    handleSizeChange(val) {
      this.pageSize = val
      this.pageNum = 1
      this.fetchTableData()
    },
    
    // 当前页改变
    handleCurrentChange(val) {
      this.pageNum = val
      this.fetchTableData()
    },
    
    // 查看详情
    handleViewDetail(row) {
      this.detailData = { ...row }
      this.detailDialogVisible = true
      this.activeField = '1'
      this.activeTab = 'detail'
      
      // 等待DOM更新后初始化图表
      this.$nextTick(() => {
        this.initDetailChart()
      })
    },
    
    // 关闭详情
    handleCloseDetail() {
      this.detailDialogVisible = false
      if (this.detailChartInstance) {
        this.detailChartInstance.dispose()
        this.detailChartInstance = null
      }
    },
    
    // 字段选择
    handleFieldSelect(key) {
      this.activeField = key
      // 根据选择的字段更新图表数据
      this.updateDetailChartData()
    },
    
    // 初始化主图表
    initMainChart() {
      if (!this.$refs.chartContainer) {
        console.error('图表容器未找到')
        return
      }
      
      // 先销毁之前的实例
      if (this.chartInstance) {
        this.chartInstance.dispose()
      }
      
      this.chartInstance = echarts.init(this.$refs.chartContainer)
      
      // 确保图表容器有高度
      setTimeout(() => {
        this.updateMainChart()
      }, 100)
    },
    
    // 更新主图表
    updateMainChart() {
      if (!this.chartInstance) {
        console.log('图表实例不存在，正在初始化...')
        this.initMainChart()
        return
      }
      
      if (this.tableData.length === 0) {
        console.log('没有数据，无法更新图表')
        this.chartInstance.clear()
        return
      }
      
      const chartData = this.prepareMainChartData()
      const option = this.getMainChartOption(chartData)
      
      this.chartInstance.setOption(option, true)
      
      // 响应窗口大小变化
      window.addEventListener('resize', () => {
        this.chartInstance.resize()
      })
    },
    
    // 准备主图表数据
    prepareMainChartData() {
      const { chartField } = this
      const chartData = []
      
      // 使用当前表格数据
      this.tableData.forEach(item => {
        let value = item[chartField] || 0
        
        // 如果是金额类数据，转换为数值
        if (chartField.includes('Amt') || chartField === 'grossProfit') {
          if (typeof value === 'string') {
            value = parseFloat(value.replace(/[￥,]/g, '')) || 0
          }
        }
        
        chartData.push({
          name: item.costname || '未知项目',
          value: value
        })
      })
      
      return chartData
    },
    
    // 获取主图表配置
    getMainChartOption(data) {
      const chartType = this.chartType
      const fieldLabel = this.getFieldLabel(this.chartField)
      
      const baseOption = {
        backgroundColor: '#fff',
        tooltip: {
          trigger: 'item',
          formatter: function(params) {
            if (chartType === 'scatter') {
              return `${params.data.name}: ${params.data.value}`
            }
            return `${params.data.name}: ${params.data.value}`
          }
        },
        title: {
          text: `各处置项目${fieldLabel}对比分析`,
          left: 'center',
          top: 20,
          textStyle: {
            fontSize: 16,
            fontWeight: 'bold'
          }
        }
      }
      
      switch (chartType) {
        case 'polarBar':
          return {
            ...baseOption,
            polar: {
              radius: [30, '80%']
            },
            angleAxis: {
              type: 'category',
              data: data.map(item => item.name),
              startAngle: 75
            },
            radiusAxis: {
              type: 'value',
              min: 0
            },
            series: [{
              type: 'bar',
              data: data.map(item => item.value),
              coordinateSystem: 'polar',
              label: {
                show: true,
                position: 'middle',
                formatter: '{b}\n{c}',
                fontSize: 10
              },
              itemStyle: {
                color: function(params) {
                  const colors = [
                    '#1890ff', '#13c2c2', '#52c41a', '#faad14', 
                    '#f5222d', '#eb2f96', '#722ed1', '#2f54eb',
                    '#fa8c16', '#a0d911', '#d4380d', '#096dd9',
                    '#08979c', '#7cb305'
                  ]
                  return colors[params.dataIndex % colors.length]
                }
              }
            }],
            legend: {
              show: false
            }
          }
          
        case 'line':
          return {
            ...baseOption,
            xAxis: {
              type: 'category',
              data: data.map(item => item.name),
              axisLabel: {
                interval: 0,
                rotate: 45,
                fontSize: 10
              }
            },
            yAxis: {
              type: 'value',
              axisLabel: {
                formatter: '{value}'
              }
            },
            series: [{
              data: data.map(item => item.value),
              type: 'line',
              smooth: true,
              lineStyle: {
                color: '#1890ff',
                width: 3
              },
              itemStyle: {
                color: '#1890ff'
              },
              areaStyle: {
                color: 'rgba(24, 144, 255, 0.1)'
              },
              symbol: 'circle',
              symbolSize: 8,
              label: {
                show: true,
                position: 'top',
                fontSize: 10
              }
            }]
          }
          
        case 'lineSort':
          // 折线图（动态排序折线图）
          return this.getDynamicSortLineOption(data, fieldLabel)
          
        case 'pie':
          return {
            ...baseOption,
            tooltip: {
              ...baseOption.tooltip,
              formatter: '{b}: {c} ({d}%)'
            },
            legend: {
              type: 'scroll',
              orient: 'vertical',
              right: 10,
              top: 50,
              bottom: 20
            },
            series: [{
              name: '指标分布',
              type: 'pie',
              radius: ['40%', '70%'],
              center: ['40%', '50%'],
              data: data,
              emphasis: {
                itemStyle: {
                  shadowBlur: 10,
                  shadowOffsetX: 0,
                  shadowColor: 'rgba(0, 0, 0, 0.5)'
                }
              },
              itemStyle: {
                borderRadius: 6,
                borderColor: '#fff',
                borderWidth: 2
              },
              label: {
                formatter: '{b}: {c}',
                fontSize: 10
              }
            }]
          }
          
        case 'bar':
          return {
            ...baseOption,
            xAxis: {
              type: 'category',
              data: data.map(item => item.name),
              axisLabel: {
                interval: 0,
                rotate: 45,
                fontSize: 10
              }
            },
            yAxis: {
              type: 'value',
              axisLabel: {
                formatter: '{value}'
              }
            },
            series: [{
              data: data.map(item => item.value),
              type: 'bar',
              label: {
                show: true,
                position: 'top',
                fontSize: 10
              },
              itemStyle: {
                color: function(params) {
                  const colors = [
                    '#1890ff', '#13c2c2', '#52c41a', '#faad14', 
                    '#f5222d', '#eb2f96', '#722ed1', '#2f54eb',
                    '#fa8c16', '#a0d911', '#d4380d', '#096dd9',
                    '#08979c', '#7cb305'
                  ]
                  return colors[params.dataIndex % colors.length]
                }
              }
            }]
          }
          
        case 'heatmap':
          // 相关矩阵（热力图，矩阵坐标系）
          return this.getHeatmapOption(data, fieldLabel)
          
        case 'sankey':
          // 桑基图（桑基图左对齐布局）
          return this.getSankeyOption(data, fieldLabel)
          
        default:
          return baseOption
      }
    },
    
    // 获取字段标签
    getFieldLabel(key) {
      const fieldMap = {
        'totalAmt': '总营业收入',
        'totalTimes': '服务总次数',
        'totalNum': '接诊人次',
        'grossProfitRate': '毛利率',
        'grossProfit': '项目毛利'
      }
      return fieldMap[key] || key
    },
    
    // 初始化详情图表
    initDetailChart() {
      if (!this.$refs.detailChartContainer) {
        console.error('详情图表容器未找到')
        return
      }
      
      // 先销毁之前的实例
      if (this.detailChartInstance) {
        this.detailChartInstance.dispose()
      }
      
      this.detailChartInstance = echarts.init(this.$refs.detailChartContainer)
      this.updateDetailChartData()
    },
    
    // 更新详情图表数据
    updateDetailChartData() {
      if (!this.detailChartInstance) {
        console.log('详情图表实例不存在，正在初始化...')
        this.initDetailChart()
        return
      }
      
      const fieldMap = {
        '1': { name: '接诊人次', value: this.detailData.totalNum || 0 },
        '2': { name: '服务总次数', value: this.detailData.totalTimes || 0 },
        '3': { name: '总营业收入', value: this.detailData.totalAmt || 0 },
        '4': { name: '总成本', value: this.detailData.totalCostAmt || 0 },
        '5': { name: '项目毛利', value: this.detailData.grossProfit || 0 },
        '6': { name: '毛利率', value: this.detailData.grossProfitRate || 0 }
      }
      
      const currentField = fieldMap[this.activeField]
      const chartData = this.generateChartData(currentField.name, currentField.value)
      
      let option = {}
      
      switch (this.detailChartType) {
        case 'polarBar':
          option = this.getDetailPolarBarOption(chartData)
          break
        case 'line':
          option = this.getDetailLineOption(chartData)
          break
        case 'pie':
          option = this.getDetailPieOption(chartData)
          break
        case 'bar':
          option = this.getDetailBarOption(chartData)
          break
        case 'scatter':
          option = this.getDetailScatterOption(chartData)
          break
        default:
          option = this.getDetailPolarBarOption(chartData)
      }
      
      this.detailChartInstance.setOption(option, true)
    },
    
    // 更新详情图表
    updateDetailChart() {
      this.updateDetailChartData()
    },
    
    // 生成图表数据
    generateChartData(name, value) {
      // 模拟多维度数据
      const data = [
        { name: `${name}_本月`, value: value * 0.8 },
        { name: `${name}_上月`, value: value * 0.7 },
        { name: `${name}_去年同期`, value: value * 0.6 },
        { name: `${name}_本年累计`, value: value * 1.2 }
      ]
      
      return data
    },
    
	// 折线图（动态排序折线图）
	getDynamicSortLineOption(data, fieldLabel) {
	  // 排序数据
	  const sortedData = [...data].sort((a, b) => b.value - a.value)
	  
	  return {
	    backgroundColor: '#fff',
	    tooltip: {
	      trigger: 'item',
	      formatter: '{b}: {c}'
	    },
	    title: {
	      text: `各处置项目${fieldLabel}动态排序分析`,
	      left: 'center',
	      top: 20,
	      textStyle: {
	        fontSize: 16,
	        fontWeight: 'bold'
	      }
	    },
	    xAxis: {
	      type: 'category',
	      data: sortedData.map(item => item.name),
	      axisLabel: {
	        interval: 0,
	        rotate: 45,
	        fontSize: 10
	      }
	    },
	    yAxis: {
	      type: 'value',
	      axisLabel: {
	        formatter: '{value}'
	      }
	    },
	    series: [{
	      data: sortedData.map(item => item.value),
	      type: 'line',
	      smooth: true,
	      lineStyle: {
	        color: '#1890ff',
	        width: 3
	      },
	      itemStyle: {
	        color: '#1890ff'
	      },
	      areaStyle: {
	        color: 'rgba(24, 144, 255, 0.1)'
	      },
	      symbol: 'circle',
	      symbolSize: 8,
	      label: {
	        show: true,
	        position: 'top',
	        fontSize: 10
	      }
	    }],
	    animation: true,
	    animationDuration: 1000,
	    animationEasing: 'cubicInOut'
	  }
	},
	
	// 相关矩阵（热力图，矩阵坐标系）
	getHeatmapOption(data, fieldLabel) {
	  // 提取前5个指标作为热力图的维度
	  const heatmapFields = this.getHeatmapFields()
	  const heatmapData = this.tableData.slice(0, 5).map(item => {
	    return heatmapFields.map(field => item[field.key] || 0)
	  })
	  
	  // 计算相关系数矩阵
	  const correlationMatrix = this.calculateCorrelationMatrix(heatmapData)
	  
	  return {
	    backgroundColor: '#fff',
	    tooltip: {
	      position: 'top',
	      formatter: function(params) {
	        return `X: ${heatmapFields[params.data[0]].label}<br/>
	                Y: ${heatmapFields[params.data[1]].label}<br/>
	                相关系数: ${params.data[2].toFixed(3)}`
	      }
	    },
	    title: {
	      text: '处置项目指标相关矩阵',
	      left: 'center',
	      top: 20,
	      textStyle: {
	        fontSize: 16,
	        fontWeight: 'bold'
	      }
	    },
	    grid: {
	      top: '10%',
	      right: '10%',
	      bottom: '15%',
	      left: '10%'
	    },
	    xAxis: {
	      type: 'category',
	      data: heatmapFields.map(f => f.label),
	      splitArea: {
	        show: true
	      },
	      axisLabel: {
	        interval: 0,
	        rotate: 45
	      }
	    },
	    yAxis: {
	      type: 'category',
	      data: heatmapFields.map(f => f.label),
	      splitArea: {
	        show: true
	      }
	    },
	    visualMap: {
	      min: -1,
	      max: 1,
	      calculable: true,
	      orient: 'horizontal',
	      left: 'center',
	      bottom: '0%',
	      inRange: {
	        color: ['#313695', '#4575b4', '#74add1', '#abd9e9', '#e0f3f8', '#ffffbf', '#fee090', '#fdae61', '#f46d43', '#d73027', '#a50026']
	      }
	    },
	    series: [{
	      name: '相关矩阵',
	      type: 'heatmap',
	      data: (() => {
	        const result = []
	        for (let i = 0; i < heatmapFields.length; i++) {
	          for (let j = 0; j < heatmapFields.length; j++) {
	            result.push([i, j, correlationMatrix[i][j]])
	          }
	        }
	        return result
	      })(),
	      label: {
	        show: true,
	        formatter: function(params) {
	          return params.data[2].toFixed(2)
	        }
	      },
	      emphasis: {
	        itemStyle: {
	          shadowBlur: 10,
	          shadowColor: 'rgba(0, 0, 0, 0.5)'
	        }
	      }
	    }]
	  }
	},
	
	// 获取热力图字段
	getHeatmapFields() {
	  return [
	    { key: 'totalAmt', label: '总营业收入' },
	    { key: 'totalTimes', label: '服务总次数' },
	    { key: 'totalNum', label: '接诊人次' },
	    { key: 'grossProfitRate', label: '毛利率' },
	    { key: 'grossProfit', label: '项目毛利' }
	  ]
	},
	
	// 计算相关系数矩阵
	calculateCorrelationMatrix(data) {
	  const matrix = []
	  const n = data[0] ? data[0].length : 0
	  
	  for (let i = 0; i < n; i++) {
	    matrix[i] = []
	    for (let j = 0; j < n; j++) {
	      // 提取第i列和第j列的数据
	      const columnI = data.map(row => row[i] || 0)
	      const columnJ = data.map(row => row[j] || 0)
	      
	      // 计算相关系数
	      matrix[i][j] = this.calculateCorrelation(columnI, columnJ)
	    }
	  }
	  
	  return matrix
	},
	
	// 计算两个数组的相关系数
	calculateCorrelation(x, y) {
	  const n = x.length
	  let sumX = 0, sumY = 0, sumXY = 0
	  let sumX2 = 0, sumY2 = 0
	  
	  for (let i = 0; i < n; i++) {
	    sumX += x[i]
	    sumY += y[i]
	    sumXY += x[i] * y[i]
	    sumX2 += x[i] * x[i]
	    sumY2 += y[i] * y[i]
	  }
	  
	  const numerator = n * sumXY - sumX * sumY
	  const denominator = Math.sqrt((n * sumX2 - sumX * sumX) * (n * sumY2 - sumY * sumY))
	  
	  return denominator === 0 ? 0 : numerator / denominator
	},
	
	// 桑基图（桑基图左对齐布局）
	getSankeyOption(data, fieldLabel) {
	  // 创建桑基图数据
	  const sankeyData = []
	  const sankeyLinks = []
	  
	  // 使用前5个数据点
	  const limitedData = data.slice(0, 5)
	  
	  limitedData.forEach((item, index) => {
	    sankeyData.push({
	      name: item.name,
	      value: item.value
	    })
	    
	    // 创建流向
	    if (index < limitedData.length - 1) {
	      sankeyLinks.push({
	        source: item.name,
	        target: limitedData[index + 1].name,
	        value: Math.min(item.value, limitedData[index + 1].value) * 0.8
	      })
	    }
	  })
	  
	  return {
	    backgroundColor: '#fff',
	    tooltip: {
	      trigger: 'item',
	      formatter: '{b}: {c}'
	    },
	    title: {
	      text: `处置项目${fieldLabel}流向分析`,
	      left: 'center',
	      top: 20,
	      textStyle: {
	        fontSize: 16,
	        fontWeight: 'bold'
	      }
	    },
	    series: [{
	      type: 'sankey',
	      layout: 'none',
	      data: sankeyData,
	      links: sankeyLinks,
	      left: '5%',
	      right: '20%',
	      top: '5%',
	      bottom: '5%',
	      nodeWidth: 20,
	      nodeGap: 8,
	      layoutIterations: 32,
	      label: {
	        show: true,
	        position: 'right',
	        fontSize: 10
	      },
	      lineStyle: {
	        color: 'source',
	        curveness: 0.5,
	        opacity: 0.6
	      },
	      itemStyle: {
	        borderColor: '#fff',
	        borderWidth: 1
	      },
	      emphasis: {
	        focus: 'adjacency',
	        lineStyle: {
	          opacity: 0.9
	        }
	      }
	    }]
	  }
	},
	
    // 极坐标柱状图配置
    getDetailPolarBarOption(data) {
      return {
        tooltip: {
          trigger: 'item',
          formatter: '{b}: {c}'
        },
        angleAxis: {
          type: 'category',
          data: data.map(item => item.name),
          axisLabel: {
            rotate: 45
          }
        },
        radiusAxis: {
          type: 'value'
        },
        polar: {},
        series: [{
          type: 'bar',
          data: data.map(item => item.value),
          coordinateSystem: 'polar',
          itemStyle: {
            color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
              { offset: 0, color: '#83bff6' },
              { offset: 0.5, color: '#188df0' },
              { offset: 1, color: '#188df0' }
            ])
          }
        }]
      }
    },
    
    // 折线图配置
    getDetailLineOption(data) {
      return {
        tooltip: {
          trigger: 'axis'
        },
        xAxis: {
          type: 'category',
          data: data.map(item => item.name)
        },
        yAxis: {
          type: 'value'
        },
        series: [{
          type: 'line',
          data: data.map(item => item.value),
          itemStyle: {
            color: '#409EFF'
          },
          areaStyle: {
            color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
              { offset: 0, color: 'rgba(64, 158, 255, 0.3)' },
              { offset: 1, color: 'rgba(64, 158, 255, 0)' }
            ])
          }
        }]
      }
    },
    
    // 饼图配置
    getDetailPieOption(data) {
      return {
        tooltip: {
          trigger: 'item',
          formatter: '{b}: {c} ({d}%)'
        },
        series: [{
          type: 'pie',
          radius: ['40%', '70%'],
          data: data,
          emphasis: {
            itemStyle: {
              shadowBlur: 10,
              shadowOffsetX: 0,
              shadowColor: 'rgba(0, 0, 0, 0.5)'
            }
          }
        }]
      }
    },
    
    // 柱状图配置
    getDetailBarOption(data) {
      return {
        tooltip: {
          trigger: 'axis',
          axisPointer: {
            type: 'shadow'
          }
        },
        xAxis: {
          type: 'category',
          data: data.map(item => item.name)
        },
        yAxis: {
          type: 'value'
        },
        series: [{
          type: 'bar',
          data: data.map(item => item.value),
          itemStyle: {
            color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
              { offset: 0, color: '#83bff6' },
              { offset: 0.5, color: '#188df0' },
              { offset: 1, color: '#188df0' }
            ])
          }
        }]
      }
    },
    
    // 散点图配置
    getDetailScatterOption(data) {
      return {
        tooltip: {
          trigger: 'item',
          formatter: function (params) {
            return `${params.data[0]}: ${params.data[1]}`
          }
        },
        xAxis: {
          type: 'category',
          data: data.map(item => item.name)
        },
        yAxis: {
          type: 'value'
        },
        series: [{
          type: 'scatter',
          data: data.map(item => [item.name, item.value]),
          itemStyle: {
            color: '#409EFF'
          }
        }]
      }
    }
  },
  watch: {
    // 监听主选项卡切换
    activeMainTab(val) {
      if (val === 'chart') {
        this.$nextTick(() => {
          if (this.tableData.length > 0) {
            this.initMainChart()
          } else {
            console.log('没有数据，无法初始化图表')
          }
        })
      } else {
        if (this.chartInstance) {
          this.chartInstance.dispose()
          this.chartInstance = null
        }
      }
    },
    
    // 监听数据变化，如果有数据且当前是图表选项卡，则更新图表
    tableData: {
      handler(newVal) {
        if (newVal.length > 0 && this.activeMainTab === 'chart') {
          this.$nextTick(() => {
            this.initMainChart()
          })
        }
      },
      deep: true
    }
  }
}
</script>

<style lang="less" scoped>
.disposal-statistics-container {
  height: calc(100vh - 120px);
  overflow-y: auto;
  padding: 16px;
  box-sizing: border-box;
  background-color: #f5f7fa;
  display: flex;
  flex-direction: column;
  
  .header-section {
    margin-bottom: 10px;
    
    .main-tabs {
      // 重置Element UI默认样式
      ::v-deep .el-tabs__header {
        margin: 0;
        border-bottom: 1px solid #e4e7ed;
      }
      
      ::v-deep .el-tabs__nav-wrap {
        &::after {
          display: none; // 隐藏默认的底部边框
        }
      }
      
      ::v-deep .el-tabs__item {
        font-size: 16px;
        font-weight: bold;
        color: #333;
        padding: 0 30px;
        height: 40px;
        line-height: 40px;
        position: relative;
        border: none;
        background: transparent;
        transition: all 0.3s;
        
        &:hover {
          color: #1890ff;
        }
        
        // 非激活选项卡样式
        &.is-active {
          color: #1890ff;
          background-color: #e6f7ff; // 浅蓝色背景
          border: 1px solid #bae7ff;
          border-bottom: none;
          border-radius: 4px 4px 0 0;
          
          // 激活选项卡的下方连接线
          &::after {
            content: '';
            position: absolute;
            bottom: -1px;
            left: 0;
            right: 0;
            height: 2px;
            background-color: #1890ff;
            z-index: 3;
          }
        }
      }
      
      // 自定义激活条
      ::v-deep .el-tabs__active-bar {
        display: none; // 隐藏默认激活条
      }
    }
  }
  
  .filter-section {
    
    .filter-container {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 16px;
      
      .filter-left {
        display: flex;
        align-items: center;
        flex-wrap: wrap;
        gap: 16px;
        
        .filter-item {
          display: flex;
          align-items: center;
          
          .filter-label {
            font-size: 14px;
            color: #333;
            margin-right: 8px;
            white-space: nowrap;
          }
        }
      }
      
      .filter-right {
        display: flex;
        gap: 8px;
      }
    }
  }
  
  .main-content-section {
    flex: 1;
    display: flex;
    flex-direction: column;
    min-height: 0;
    
    // 加载状态
    .loading-container,
    .empty-container {
      flex: 1;
      display: flex;
      flex-direction: column;
      justify-content: center;
      align-items: center;
      padding: 50px 0;
    }
    
    // 表格区域
    .table-section {
      flex: 1;
      display: flex;
      flex-direction: column;
      min-height: 0;
      height: 100%;
      width: 100%;
      
      .table-container {
        flex: 1;
        display: flex;
        flex-direction: column;
        min-height: 0;
        
        .el-table {
          flex: 1;
          overflow: auto;
        }
        
        .pagination-container {
          margin-top: 16px;
          padding: 10px 0;
          background-color: #fff;
          border-top: 1px solid #e6e6e6;
          
          &.left-pagination {
            display: flex;
            justify-content: flex-start;
            
            .el-pagination {
              margin: 0;
            }
          }
        }
      }
    }
    
    // 图表控制区域
    .chart-control-section {
      margin-bottom: 16px;
      
      .chart-controls {
        display: flex;
        align-items: center;
      }
    }
    
    // 图表内容区域
    .chart-content-section {
      flex: 1;
      display: flex;
      flex-direction: column;
      min-height: 0;
      
      .chart-loading,
      .chart-empty {
        flex: 1;
        display: flex;
        flex-direction: column;
        justify-content: center;
        align-items: center;
        border: 1px solid #e8e8e8;
        border-radius: 4px;
        padding: 50px 0;
        background: #fff;
      }
      
      .chart-container {
        flex: 1;
        min-height: 0;
        border: 1px solid #e8e8e8;
        border-radius: 4px;
        padding: 10px;
        background: #fff;
        
        // 确保ECharts容器有明确的高度
        > div {
          width: 100%;
          height: 100%;
          min-height: 400px; // 添加最小高度以确保可见
        }
      }
    }
  }
}

// 详情模态框样式
::v-deep .detail-dialog {
  border-radius: 8px;
  overflow: hidden;
  .el-dialog__header {
    background: linear-gradient(135deg, #ebfaff 0%, #00557f 100%);
    color: #00557f;
    border-bottom: 1px solid #ebeef5;
    border-radius: 8px 8px 0 0;
    
    .el-dialog__title {
      color: white;
      font-weight: bold;
    }
    
    .el-dialog__headerbtn {
      .el-dialog__close {
        color: white;
        
        &:hover {
          color: #f0f0f0;
        }
      }
    }
  }
  .el-dialog__body {
    padding: 12px;
	}
  .detail-container {
    display: flex;
    gap: 20px;
    
    .detail-left {
      width: 280px;
      background-color: #fff;
      border-radius: 8px;
      padding: 20px;
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
      
      .dept-header {
        margin-bottom: 20px;
        
        .dept-avatar-section {
          display: flex;
          align-items: center;
		  flex-direction: column;
		  justify-content: center;
          gap: 12px;
          
          .dept-avatar {
            border: 2px solid #e6e6e6;
            box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
          }
          
          .dept-info {
            .dept-name {
              font-size: 16px;
              font-weight: bold;
              color: #333;
              margin-bottom: 4px;
            }
            
            .dept-status {
              .el-tag {
                font-size: 10px;
                padding: 2px 6px;
              }
            }
          }
        }
      }
      
      .field-list {
        .field-menu {
          border: none;
          .field-count {
            font-size: 12px;
            color: #909399;
            margin-left: 4px;
          }
		  /deep/ .el-menu-item, .el-submenu__title{
		  	font-size: 12px;
			height:40px;
			line-height:40px;
		  }
        }
      }
    }
    
    .detail-right {
      flex: 1;
      display: flex;
      flex-direction: column;
      
      .detail-tabs {
        flex: 1;
        display: flex;
        flex-direction: column;
        
        .el-tab-pane {
          flex: 1;
          overflow: auto;
        }
      }
    }
  }
  
  .indicator-section {
    margin-top: 20px;
    
    .el-descriptions {
      ::v-deep .el-descriptions-item__content {
        .currency-amount {
          font-weight: 500;
          color: #333;
          font-size: 12px;
          &::before {
            content: '￥';
            margin-right: 2px;
          }
        }
      }
    }
  }
  
  .chart-content {
    margin-top: 20px;
    height: 100%;
    display: flex;
    flex-direction: column;
    
    .chart-controls {
      margin-bottom: 16px;
    }
    
    .detail-chart-container {
      flex: 1;
      min-height: 400px;
    }
  }
}

// 表格行样式
.table-row-class {
  &:hover {
    background-color: #f5f7fa;
  }
}

// 响应式设计
@media (max-width: 1200px) {
  .disposal-statistics-container {
    .detail-container {
      flex-direction: column;
      height: auto;
      
      .detail-left {
        width: 100%;
        margin-bottom: 20px;
      }
    }
  }
}

@media (max-width: 768px) {
  .disposal-statistics-container {
    .filter-container {
      flex-direction: column;
      gap: 16px;
      
      .filter-left {
        flex-wrap: wrap;
      }
      
      .filter-right {
        width: 100%;
        justify-content: center;
      }
    }
    
    .pagination-container {
      &.left-pagination {
        justify-content: center !important;
      }
    }
  }
}
</style>